import React from 'react';
import { useHistory } from 'react-router-dom';
import { useDemo } from '../../state/DemoContext';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/helpers';
import './investordashboard.css';

const InvestorDashboard = () => {
  const history = useHistory();
  const { state, actions } = useDemo();

  const currentInvestor = state.currentInvestor;
  const investorCommitments = state.commitments.filter(c => c.investorId === currentInvestor?.id);
  // const investorListings = state.listings.filter(l => 
  //   investorCommitments.some(c => c.listingId === l.id)
  // ); // Unused variable

  if (!currentInvestor) {
    return (
      <div className="jo-xyz-demo-investor-dashboard-page">
        <div className="jo-xyz-demo-container">
          <div className="jo-xyz-demo-no-investor-state">
            <div className="jo-xyz-demo-card">
              <div className="jo-xyz-demo-empty-state">
                <div className="jo-xyz-demo-empty-state__icon">💰</div>
                <h3>No Investor Account</h3>
                <p>You need to create an investor account to access the dashboard.</p>
                <button
                  className="jo-xyz-demo-btn jo-xyz-demo-btn--primary"
                  onClick={() => history.push('/jo-xyz-demo/investor/signup')}
                >
                  Create Investor Account
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getTotalCommitted = () => {
    return investorCommitments.reduce((sum, c) => sum + c.amount, 0);
  };

  const getActiveCommitments = () => {
    return investorCommitments.filter(c => c.status === 'Committed').length;
  };

  const getCompatibleListings = () => {
    return state.listings.filter(listing => {
      const property = state.properties.find(p => p.id === listing.assetId);
      if (!property) return false;
      
      // Check if listing matches investor mandates
      const mandates = currentInvestor.mandates;
      
      if (!mandates.assetTypes.includes('Property')) return false;
      if (listing.ltv > mandates.maxLtv) return false;
      if (listing.requestedAmount < mandates.minTicket || listing.requestedAmount > mandates.maxTicket) return false;
      
      const listingState = listing.location.split(',')[1]?.trim();
      if (!mandates.geos.includes(listingState)) return false;
      
      if (mandates.lienPreference !== 'Any' && !listing.badges.includes(mandates.lienPreference)) return false;
      if (listing.desiredTermMonths < mandates.termRange[0] || listing.desiredTermMonths > mandates.termRange[1]) return false;
      if (property.capRate < mandates.minCapRate) return false;
      
      return true;
    });
  };

  const compatibleListings = getCompatibleListings();
  const totalCommitted = getTotalCommitted();
  const activeCommitments = getActiveCommitments();

  return (
    <div className="jo-xyz-demo-investor-dashboard-page">
      <div className="jo-xyz-demo-investor-dashboard-page__header">
        <div className="jo-xyz-demo-investor-dashboard-page__header-left">
          <h1>Investor Dashboard</h1>
          <p className="jo-xyz-demo-investor-dashboard-page__subtitle">
            Welcome back, {currentInvestor.name}
          </p>
        </div>
        <div className="jo-xyz-demo-investor-dashboard-page__header-right">
          <button
            className="jo-xyz-demo-btn jo-xyz-demo-btn--secondary"
            onClick={() => history.push('/jo-xyz-demo/marketplace')}
          >
            Browse Marketplace
          </button>
        </div>
      </div>

      {/* Investor Stats */}
      <div className="jo-xyz-demo-investor-dashboard-page__stats">
        <div className="jo-xyz-demo-grid jo-xyz-demo-grid--4">
          <div className="jo-xyz-demo-stat-card">
            <div className="jo-xyz-demo-stat-card__content">
              <div className="jo-xyz-demo-stat-card__value">{formatCurrency(totalCommitted)}</div>
              <div className="jo-xyz-demo-stat-card__label">Total Committed</div>
            </div>
          </div>
          <div className="jo-xyz-demo-stat-card">
            <div className="jo-xyz-demo-stat-card__content">
              <div className="jo-xyz-demo-stat-card__value">{activeCommitments}</div>
              <div className="jo-xyz-demo-stat-card__label">Active Commitments</div>
            </div>
          </div>
          <div className="jo-xyz-demo-stat-card">
            <div className="jo-xyz-demo-stat-card__content">
              <div className="jo-xyz-demo-stat-card__value">{compatibleListings.length}</div>
              <div className="jo-xyz-demo-stat-card__label">Compatible Listings</div>
            </div>
          </div>
          <div className="jo-xyz-demo-stat-card">
            <div className="jo-xyz-demo-stat-card__content">
              <div className="jo-xyz-demo-stat-card__value">{currentInvestor.type}</div>
              <div className="jo-xyz-demo-stat-card__label">Investor Type</div>
            </div>
          </div>
        </div>
      </div>

      <div className="jo-xyz-demo-investor-dashboard-page__content">
        <div className="jo-xyz-demo-investor-dashboard-page__left">
          {/* My Commitments */}
          <div className="jo-xyz-demo-investor-dashboard-page__section">
            <div className="jo-xyz-demo-card">
              <div className="jo-xyz-demo-card__header">
                <h3 className="jo-xyz-demo-card__title">My Commitments</h3>
                <span className="jo-xyz-demo-commitments-count">
                  {investorCommitments.length} commitment{investorCommitments.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="jo-xyz-demo-commitments-list">
                {investorCommitments.length === 0 ? (
                  <div className="jo-xyz-demo-no-commitments">
                    <p>You haven't made any commitments yet.</p>
                    <button
                      className="jo-xyz-demo-btn jo-xyz-demo-btn--primary"
                      onClick={() => history.push('/jo-xyz-demo/marketplace')}
                    >
                      Browse Opportunities
                    </button>
                  </div>
                ) : (
                  investorCommitments.map((commitment) => {
                    const listing = state.listings.find(l => l.id === commitment.listingId);
                    const property = state.properties.find(p => p.id === listing?.assetId);

                    return (
                      <div key={commitment.id} className="jo-xyz-demo-commitment-item">
                        <div className="jo-xyz-demo-commitment-item__header">
                          <div className="jo-xyz-demo-commitment-item__info">
                            <h4>{listing?.title || 'Unknown Listing'}</h4>
                            <p>{property?.address || 'Unknown Property'}</p>
                          </div>
                          <div className="jo-xyz-demo-commitment-item__amount">
                            {formatCurrency(commitment.amount)}
                          </div>
                        </div>
                        <div className="jo-xyz-demo-commitment-item__details">
                          <div className="jo-xyz-demo-commitment-meta">
                            <span>Committed: {formatDateTime(commitment.createdAt)}</span>
                            <span>Status: {commitment.status}</span>
                            <span>LTV: {listing ? (listing.ltv * 100).toFixed(1) : 'N/A'}%</span>
                          </div>
                          {commitment.notes && (
                            <div className="jo-xyz-demo-commitment-notes">
                              <strong>Notes:</strong> {commitment.notes}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Investment Mandates */}
          <div className="jo-xyz-demo-investor-dashboard-page__section">
            <div className="jo-xyz-demo-card">
              <div className="jo-xyz-demo-card__header">
                <h3 className="jo-xyz-demo-card__title">Investment Mandates</h3>
                <button
                  className="jo-xyz-demo-btn jo-xyz-demo-btn--secondary jo-xyz-demo-btn--sm"
                  onClick={() => actions.showToast('Mandate editing coming soon!', 'info')}
                >
                  Edit
                </button>
              </div>
              <div className="jo-xyz-demo-mandates-content">
                <div className="jo-xyz-demo-mandates-grid">
                  <div className="jo-xyz-demo-mandate-item">
                    <span className="jo-xyz-demo-mandate-label">Asset Types</span>
                    <span className="jo-xyz-demo-mandate-value">{currentInvestor.mandates.assetTypes.join(', ')}</span>
                  </div>
                  <div className="jo-xyz-demo-mandate-item">
                    <span className="jo-xyz-demo-mandate-label">Max LTV</span>
                    <span className="jo-xyz-demo-mandate-value">{(currentInvestor.mandates.maxLtv * 100).toFixed(0)}%</span>
                  </div>
                  <div className="jo-xyz-demo-mandate-item">
                    <span className="jo-xyz-demo-mandate-label">Ticket Range</span>
                    <span className="jo-xyz-demo-mandate-value">
                      {formatCurrency(currentInvestor.mandates.minTicket)} - {formatCurrency(currentInvestor.mandates.maxTicket)}
                    </span>
                  </div>
                  <div className="jo-xyz-demo-mandate-item">
                    <span className="jo-xyz-demo-mandate-label">Geographic Focus</span>
                    <span className="jo-xyz-demo-mandate-value">{currentInvestor.mandates.geos.join(', ')}</span>
                  </div>
                  <div className="jo-xyz-demo-mandate-item">
                    <span className="jo-xyz-demo-mandate-label">Lien Preference</span>
                    <span className="jo-xyz-demo-mandate-value">{currentInvestor.mandates.lienPreference}</span>
                  </div>
                  <div className="jo-xyz-demo-mandate-item">
                    <span className="jo-xyz-demo-mandate-label">Term Range</span>
                    <span className="jo-xyz-demo-mandate-value">
                      {currentInvestor.mandates.termRange[0]} - {currentInvestor.mandates.termRange[1]} months
                    </span>
                  </div>
                  <div className="jo-xyz-demo-mandate-item">
                    <span className="jo-xyz-demo-mandate-label">Min Cap Rate</span>
                    <span className="jo-xyz-demo-mandate-value">{(currentInvestor.mandates.minCapRate * 100).toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="jo-xyz-demo-investor-dashboard-page__right">
          {/* Compatible Listings */}
          <div className="jo-xyz-demo-investor-dashboard-page__section">
            <div className="jo-xyz-demo-card">
              <div className="jo-xyz-demo-card__header">
                <h3 className="jo-xyz-demo-card__title">Compatible Listings</h3>
                <span className="jo-xyz-demo-compatible-count">
                  {compatibleListings.length} match{compatibleListings.length !== 1 ? 'es' : ''}
                </span>
              </div>
              <div className="jo-xyz-demo-compatible-listings">
                {compatibleListings.length === 0 ? (
                  <div className="jo-xyz-demo-no-compatible">
                    <p>No listings match your current mandates.</p>
                    <button
                      className="jo-xyz-demo-btn jo-xyz-demo-btn--secondary"
                      onClick={() => actions.showToast('Mandate editing coming soon!', 'info')}
                    >
                      Update Mandates
                    </button>
                  </div>
                ) : (
                  <div className="jo-xyz-demo-listings-preview">
                    {compatibleListings.slice(0, 3).map((listing) => {
                      const property = state.properties.find(p => p.id === listing.assetId);
                      return (
                        <div key={listing.id} className="jo-xyz-demo-listing-preview">
                          <div className="jo-xyz-demo-listing-preview__header">
                            <h4>{listing.title}</h4>
                            <span className="jo-xyz-demo-listing-amount">{formatCurrency(listing.requestedAmount)}</span>
                          </div>
                          <div className="jo-xyz-demo-listing-preview__details">
                            <p>{property?.address}</p>
                            <div className="jo-xyz-demo-listing-meta">
                              <span>LTV: {(listing.ltv * 100).toFixed(1)}%</span>
                              <span>Term: {listing.desiredTermMonths} months</span>
                            </div>
                          </div>
                          <button
                            className="jo-xyz-demo-btn jo-xyz-demo-btn--primary jo-xyz-demo-btn--sm"
                            onClick={() => history.push(`/jo-xyz-demo/asset/${listing.id}`)}
                          >
                            View Details
                          </button>
                        </div>
                      );
                    })}
                    {compatibleListings.length > 3 && (
                      <div className="jo-xyz-demo-view-all">
                        <button
                          className="jo-xyz-demo-btn jo-xyz-demo-btn--secondary"
                          onClick={() => history.push('/jo-xyz-demo/marketplace')}
                        >
                          View All {compatibleListings.length} Listings
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Profile Information */}
          <div className="jo-xyz-demo-investor-dashboard-page__section">
            <div className="jo-xyz-demo-card">
              <div className="jo-xyz-demo-card__header">
                <h3 className="jo-xyz-demo-card__title">Profile Information</h3>
              </div>
              <div className="jo-xyz-demo-profile-info">
                <div className="jo-xyz-demo-profile-item">
                  <span className="jo-xyz-demo-profile-label">Organization</span>
                  <span className="jo-xyz-demo-profile-value">{currentInvestor.name}</span>
                </div>
                <div className="jo-xyz-demo-profile-item">
                  <span className="jo-xyz-demo-profile-label">Email</span>
                  <span className="jo-xyz-demo-profile-value">{currentInvestor.email}</span>
                </div>
                <div className="jo-xyz-demo-profile-item">
                  <span className="jo-xyz-demo-profile-label">Type</span>
                  <span className="jo-xyz-demo-profile-value">{currentInvestor.type}</span>
                </div>
                <div className="jo-xyz-demo-profile-item">
                  <span className="jo-xyz-demo-profile-label">Headquarters</span>
                  <span className="jo-xyz-demo-profile-value">{currentInvestor.profile.headquarters}</span>
                </div>
                <div className="jo-xyz-demo-profile-item">
                  <span className="jo-xyz-demo-profile-label">Experience</span>
                  <span className="jo-xyz-demo-profile-value">{currentInvestor.profile.experience}</span>
                </div>
                {currentInvestor.profile.aum > 0 && (
                  <div className="jo-xyz-demo-profile-item">
                    <span className="jo-xyz-demo-profile-label">AUM</span>
                    <span className="jo-xyz-demo-profile-value">{formatCurrency(currentInvestor.profile.aum)}</span>
                  </div>
                )}
                <div className="jo-xyz-demo-profile-item">
                  <span className="jo-xyz-demo-profile-label">Member Since</span>
                  <span className="jo-xyz-demo-profile-value">{formatDate(currentInvestor.createdAt)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestorDashboard;
